/**
 * KAN-341: действующий тариф — что подключено, до какого числа и что дальше.
 *
 * Срок обязателен на экране. Без него абонентка выглядит разовой покупкой:
 * клиент не ждёт счёта, а однажды упирается в отказ и считает это поломкой.
 *
 * ⭐ «Действует ли тариф» берётся ГОТОВЫМ с бэкенда (`has_access`), а не
 * выводится здесь из статуса и дат. Правило одно, считает его гейт — а
 * посчитав второй раз тут, кабинет однажды показал бы «Активна» там, где
 * запросы уже отклоняются.
 *
 * KAN-989: абонентка списывается с кошелька. Видна дата следующего списания,
 * а при PAST_DUE красная плашка с датой выключения функций (grace_ends_at
 * считает сервер) и переходом на пополнение.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { subscriptionApi } from '@/services/api/subscriptions';
import { formatSubscriptionStatus } from '@/types/subscription';
import type { OrganizationSubscription } from '@/types/subscription';
import { CancelSubscriptionButton } from './CancelSubscriptionButton';
import { TariffStorefront } from './TariffStorefront';
import { CURRENT_SUBSCRIPTION_QUERY_KEY } from '@/hooks/useTariffPurchase';
import { nextChargeDate } from '@/lib/tariffCharge';

/** Цвет статуса. Семантика, а не акцент: красный значит «работа остановлена». */
const STATUS_STYLE: Record<string, string> = {
  TRIAL: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-700',
  PAST_DUE: 'bg-amber-100 text-amber-800',
  CANCELED: 'bg-gray-100 text-gray-700',
  EXPIRED: 'bg-red-100 text-red-700',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Склонение «день» — иначе на экране регулярно «осталось 3 дней». */
function daysWord(days: number): string {
  const tail = days % 100;
  if (tail >= 11 && tail <= 14) return 'дней';
  switch (days % 10) {
    case 1:
      return 'день';
    case 2:
    case 3:
    case 4:
      return 'дня';
    default:
      return 'дней';
  }
}

export function CurrentTariff() {
  const { data: subscription, isLoading } = useQuery<OrganizationSubscription | null>({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: subscriptionApi.getCurrent,
  });

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500" data-testid="current-tariff-loading">
        Загружаем тариф…
      </p>
    );
  }

  const nextCharge = subscription ? nextChargeDate(subscription) : null;

  return (
    <div className="space-y-6" data-testid="current-tariff">
      {subscription ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Действующий тариф</CardTitle>
            <span
              data-testid="tariff-status-badge"
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                // KAN-342: отменённый, но ещё действующий тариф — не «Активна».
                // Бейдж «Активна» рядом с плашкой «Тариф отменён» читался как
                // противоречие, хотя оба утверждения верны.
                subscription.cancel_at_period_end
                  ? 'bg-amber-100 text-amber-800'
                  : STATUS_STYLE[subscription.status] || 'bg-gray-100 text-gray-700'
              }`}>
              {subscription.cancel_at_period_end
                ? 'Отменяется'
                : formatSubscriptionStatus(subscription.status)}
            </span>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-2xl font-semibold" data-testid="tariff-plan-name">
              {subscription.plan.name}
            </p>

            {subscription.status === 'PAST_DUE' && (
              <div
                className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between"
                data-testid="tariff-past-due">
                <p>
                  Не хватило средств на продление.{' '}
                  {subscription.grace_ends_at
                    ? `Функции тарифа выключатся ${formatDate(subscription.grace_ends_at)}.`
                    : 'Функции тарифа скоро выключатся.'}{' '}
                  Пополните баланс.
                </p>
                <Link
                  to="/balance"
                  data-testid="tariff-past-due-topup"
                  className="shrink-0 rounded-md bg-red-600 px-3 py-1.5 text-center font-medium text-white">
                  Пополнить баланс
                </Link>
              </div>
            )}

            <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              {subscription.current_period_start && (
                <div className="flex justify-between sm:block" data-testid="tariff-started-at">
                  <dt className="text-gray-500">Действует с</dt>
                  <dd className="font-medium tabular-nums">
                    {formatDate(subscription.current_period_start)}
                  </dd>
                </div>
              )}
              {subscription.ends_at && (
                <div className="flex justify-between sm:block" data-testid="tariff-ends-at">
                  <dt className="text-gray-500">Действует до</dt>
                  <dd className="font-medium tabular-nums">{formatDate(subscription.ends_at)}</dd>
                </div>
              )}
              {nextCharge && (
                <div className="flex justify-between sm:block" data-testid="tariff-next-charge">
                  <dt className="text-gray-500">Следующее списание с баланса</dt>
                  <dd className="font-medium tabular-nums">{formatDate(nextCharge)}</dd>
                </div>
              )}
            </dl>

            {typeof subscription.days_left === 'number' && (
              <p
                className={`text-sm ${subscription.has_access ? 'text-gray-500' : 'text-red-700'}`}
                data-testid="tariff-days-left">
                {subscription.has_access
                  ? `Осталось ${subscription.days_left} ${daysWord(subscription.days_left)}`
                  : subscription.access_message}
              </p>
            )}

            {subscription.cancel_at_period_end && (
              <p className="text-sm text-amber-800" data-testid="tariff-cancel-pending">
                Тариф отменён и доработает до конца оплаченного срока.
              </p>
            )}

            {subscription.has_access && !subscription.cancel_at_period_end && (
              <CancelSubscriptionButton subscriptionId={subscription.id} />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600" data-testid="tariff-none">
              Тариф не подключён. Выберите подходящий: абонентка спишется с баланса, и работа
              в кабинете возобновится сразу.
            </p>
          </CardContent>
        </Card>
      )}

      <TariffStorefront
        currentPlanName={subscription?.has_access ? subscription.plan.name : undefined}
        // Заперта витрина только ОПЛАЧЕННЫМ тарифом: из пробы клиент вправе
        // купить любой, иначе он заперт на все две недели пробы.
        paidTariffInForce={Boolean(subscription?.has_access && subscription.status === 'ACTIVE')}
        currentEndsAt={subscription?.ends_at}
      />
    </div>
  );
}
